import { useEffect, useState } from 'react';
import { Bell, CalendarDays, Mail, Radio, X, Zap } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input, Select } from '@/components/ui/Input';
import type { DashboardSettings } from '@/types';

interface SettingsModalProps {
  settings: DashboardSettings;
  isSaving: boolean;
  onClose: () => void;
  onSave: (settings: DashboardSettings) => void;
}

const timezoneOptions = [
  { value: 'America/New_York', label: 'Eastern Time' },
  { value: 'America/Chicago', label: 'Central Time' },
  { value: 'America/Denver', label: 'Mountain Time' },
  { value: 'America/Los_Angeles', label: 'Pacific Time' },
  { value: 'Asia/Calcutta', label: 'India Standard Time' },
  { value: 'UTC', label: 'UTC' },
];

export function SettingsModal({ settings, isSaving, onClose, onSave }: SettingsModalProps) {
  const [draft, setDraft] = useState<DashboardSettings>(settings);

  useEffect(() => {
    setDraft(settings);
  }, [settings]);

  const toggles = [
    {
      key: 'emailNotifications' as const,
      title: 'Email notifications',
      description: 'Send project updates to the primary agency contact.',
      icon: Mail,
    },
    {
      key: 'projectAnnouncements' as const,
      title: 'Project announcements',
      description: 'Show announcements about ongoing work in the alert feed.',
      icon: Radio,
    },
    {
      key: 'weeklySummary' as const,
      title: 'Weekly summary',
      description: 'Include a weekly execution summary in dashboard alerts.',
      icon: CalendarDays,
    },
    {
      key: 'urgentAlerts' as const,
      title: 'Urgent alerts',
      description: 'Highlight urgent requests and review blockers.',
      icon: Zap,
    },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-xl overflow-hidden rounded-lg bg-white shadow-2xl">
        <div className="flex items-start justify-between border-b border-slate-100 p-6">
          <div>
            <div className="flex items-center gap-2">
              <Bell className="h-5 w-5 text-blue-600" />
              <h2 className="text-xl font-bold text-slate-900">Dashboard Settings</h2>
            </div>
            <p className="mt-1 text-sm text-slate-500">Manage alerts, announcements, and dashboard preferences.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close settings"
            className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-5 p-6">
          <Input
            label="Primary contact"
            value={draft.contactName}
            onChange={(event) => setDraft((prev) => ({ ...prev, contactName: event.target.value }))}
          />

          <Select
            label="Dashboard timezone"
            value={draft.timezone}
            options={timezoneOptions}
            onChange={(event) => setDraft((prev) => ({ ...prev, timezone: event.target.value }))}
          />

          <div className="space-y-3">
            {toggles.map((toggle) => {
              const Icon = toggle.icon;

              return (
                <label
                  key={toggle.key}
                  className="flex cursor-pointer items-start gap-3 rounded-lg border border-slate-200 p-4 transition-colors hover:bg-slate-50"
                >
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-slate-900">{toggle.title}</p>
                    <p className="text-sm text-slate-500">{toggle.description}</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={draft[toggle.key]}
                    onChange={(event) =>
                      setDraft((prev) => ({ ...prev, [toggle.key]: event.target.checked }))
                    }
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-600"
                  />
                </label>
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-slate-100 bg-slate-50 p-6">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" isLoading={isSaving} onClick={() => onSave(draft)}>
            Save Settings
          </Button>
        </div>
      </div>
    </div>
  );
}
